import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../../components/AppToolbar/exportMenu.css';

/** One row of a {@link PlmRowMenu}. */
export interface PlmRowMenuItem {
  /** Stable key. */
  id: string;
  /** Row caption. */
  label: string;
  /** Tabler icon class, e.g. `ti-copy-plus`. */
  icon?: string;
  /** Runs on click / Enter / Space. */
  onSelect: () => void;
  /** Why the row does not apply: it is greyed, the reason shown on its right, and it cannot be run. */
  disabledReason?: string | null;
  /** A line above the row (a new group). */
  separator?: boolean;
  /** A destructive action (red). */
  danger?: boolean;
}

export interface PlmRowMenuProps {
  /** Viewport coordinates the menu opens at (the right-click). */
  position: { x: number; y: number };
  /** What the menu acts on, announced to assistive tech. */
  label: string;
  /** Shown at the top of the menu (prototype v18: the layer's code). */
  heading?: string;
  /** The rows, top to bottom. */
  items: PlmRowMenuItem[];
  /** Closed without a pick: Esc, a click elsewhere, a scroll or a resize. */
  onClose: () => void;
}

const EDGE = 8;

/**
 * The Sequence list's right-click menu (Add Intra-dup / Add Inter-dup — owner
 * rule 2026-10-07: dups are selected on the entry page, on the sample's own
 * row). It opens where the pointer is rather than under a trigger button, which
 * is the one thing the shared toolbar <ActionMenu> cannot do; everything else
 * is that menu's — its stylesheet and its keyboard contract (standing rule 14):
 * while open, a WINDOW capture-phase keydown drives it, so the arrows move the
 * cursor, Home / End jump, Enter / Space run the cursor row and Esc closes, and
 * no key leaks to the grid behind. Focus goes back to where it came from on
 * close, so keyboard navigation of the list carries on.
 */
export function PlmRowMenu({ position, label, heading, items, onClose }: PlmRowMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [active, setActive] = useState(0);

  // Live mirrors for the window handler, which closes over the first render.
  const activeRef = useRef(0);
  activeRef.current = active;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Whatever held focus when the menu opened (the list row) gets it back.
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => { opener.current?.focus?.(); };
  }, []);

  const run = useCallback((i: number) => {
    const it = itemsRef.current[i];
    if (!it || it.disabledReason) return;
    onClose();
    it.onSelect();
  }, [onClose]);

  // Clamp inside the viewport — the same rule as the column filter popups (§18.6).
  useLayoutEffect(() => {
    const m = menuRef.current;
    if (!m) return;
    const left = Math.max(EDGE, Math.min(position.x, window.innerWidth - m.offsetWidth - EDGE));
    const top = Math.max(EDGE, Math.min(position.y, window.innerHeight - m.offsetHeight - EDGE));
    setPos({ top, left });
  }, [position]);

  useEffect(() => { if (pos) menuRef.current?.focus(); }, [pos]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) onClose();
    };
    const dismiss = () => onClose();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const n = itemsRef.current.length;
      if (!n) return;
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === 'ArrowDown') { stop(); setActive(a => (a + 1) % n); }
      else if (e.key === 'ArrowUp') { stop(); setActive(a => (a - 1 + n) % n); }
      else if (e.key === 'Home') { stop(); setActive(0); }
      else if (e.key === 'End') { stop(); setActive(n - 1); }
      else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { stop(); run(activeRef.current); }
      else if (e.key === 'Escape' || e.key === 'Tab') { stop(); onClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [run, onClose]);

  return createPortal(
    <div
      ref={menuRef}
      className="acc-export-menu acc-export-menu-fixed plm-rowmenu"
      role="menu"
      aria-label={label}
      tabIndex={-1}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      onContextMenu={e => e.preventDefault()}
    >
      {heading ? <div className="plm-rowmenu__head">{heading}</div> : null}
      {items.map((it, i) => (
        <div key={it.id}>
          {it.separator ? <div className="plm-rowmenu__sep" role="separator" /> : null}
          <div
            role="menuitem"
            tabIndex={-1}
            aria-disabled={it.disabledReason ? true : undefined}
            className={`acc-export-item${i === active ? ' active' : ''}${it.disabledReason ? ' disabled' : ''}${it.danger ? ' plm-rowmenu__danger' : ''}`}
            onClick={() => run(i)}
            onMouseEnter={() => setActive(i)}
          >
            {it.icon ? <i className={`ti ${it.icon}`} aria-hidden="true" /> : null} {it.label}
            {it.disabledReason ? <span className="plm-rowmenu__why">{it.disabledReason}</span> : null}
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
}
